/**
 * InputCommand batch (de)serialization with redundancy. (§2.3, §2.8, §15.4)
 *
 * The client samples one InputCommand per frame at 60Hz (§2.2) and sends it on
 * an unreliable channel. A single lost packet would drop an input the server can
 * never recover, so every packet carries the LAST N inputs (§2.3): the newest
 * plus a few predecessors. One dropped packet then costs nothing — the next
 * packet re-delivers the gap. The server dedupes by `seq`.
 *
 * ── Layout (payload only; the MessageType byte is added by protocol.ts) ──────
 *
 *   count[COUNT_BITS]                         number of inputs in this batch
 *   then, per input (batch order is caller's choice; server dedupes by seq):
 *     seq[32] tick[32]                        absolute — cheap enough at this rate
 *     moveX[MOVE] moveZ[MOVE]                 [-1,1], quantized
 *     aimYaw[YAW]                             [-π,π], same 12-bit scheme as yaw
 *     liftBias[UNIT] curl[UNIT]               [-1,1]  (curl is the house curve nudge)
 *     passCharge[CHARGE] shootCharge[CHARGE]  seconds, 0..CHARGE_MAX (server re-clamps)
 *     flags[24]                               InputFlags, now reaching bit 23 (Boost2)
 *     skill[4] tactical[3]
 *
 * seq/tick are sent absolutely rather than delta-coded: at 3 inputs/packet that
 * is ~19 bytes/input, ~3.5 KB/s up — comfortably inside the 8 KB/s up budget
 * (§2.8) — and absolute coding is robust to any batch ordering or gap. A
 * delta-seq tightening is a future optimization, not a correctness need.
 *
 * CHANGING ANY WIDTH BELOW IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION (§12.6).
 */

import type { InputCommand, SkillMove, TacticalCommand } from '../sim/types.js';
import { emptyInput } from '../sim/types.js';
import { BitReader, BitWriter } from './bitwriter.js';
import {
  WIRE_SEQ_BITS,
  WIRE_SKILL_BITS,
  WIRE_TACTICAL_BITS,
  WIRE_TICK_BITS,
  WIRE_UNIT_BITS,
  WIRE_YAW_BITS,
  dequantizeUnitSigned,
  dequantizeYaw,
  quantizeFloat,
  dequantizeFloat,
  quantizeUnitSigned,
  quantizeYaw,
} from './serialize.js';

// ---------------------------------------------------------------------------
// Wire widths specific to the input frame (packet layout, not game feel).
// ---------------------------------------------------------------------------

/** Movement axis resolution. 10 bits over [-1,1] ≈ 0.002 — finer than an analog
 *  stick, so diagonal dribbling directions stay crisp. (§2.8 "8-10 bits") */
export const WIRE_MOVE_BITS = 10;

/**
 * InputFlags is a 20-bit field. Bits 0..15 were the original set (KickPlayer =
 * 1<<15); the sim wave added Jump = 1<<16 and BicycleKick = 1<<17 (types.ts), so
 * the field had to grow past 16 or those two would be silently truncated off the
 * wire — a shot input that fires locally and vanishes on the way to the server.
 *
 * ⚠ IT HAPPENED AGAIN. The two-tier boost appended Boost1 = 1<<18 and
 * Boost2 = 1<<19 to InputFlags and did NOT widen this field, so every boost
 * press was masked off in `writeInput` — the boost worked in solo/practice
 * (where inputs are never serialized) and did nothing whatsoever in an online
 * match. Found the day the AI was given the boost and the §10.1 range test
 * rejected bit 18. 20 bits covers the enum with the same margin the last
 * widening left.
 *
 * ⚠ THIRD WIDENING (20 -> 24), and this one is DELIBERATE rather than a
 * reaction: the keeper controls (dive left/right, hold) and close control need
 * four bits and the field had none spare. The guard test below is what makes
 * that a decision instead of a discovery.
 *
 * PROTOCOL_VERSION is bumped with it (§12.6) — an old client's narrower frame
 * would desync the reader here.
 */
export const WIRE_INPUT_FLAGS_BITS = 24;

/** Low-24-bit mask for the flags field. `(1<<24)-1` is well inside int32. */
const INPUT_FLAGS_MASK = (1 << WIRE_INPUT_FLAGS_BITS) - 1;

/**
 * Charge accumulators on the wire. The client charges and CLAIMS a duration; the
 * server validates and re-clamps against PASS/SHOT_CHARGE_MAX_S (§2.9), so the
 * wire only needs to carry a bounded claim. 0..1.5s in 8 bits ≈ 5.9ms steps —
 * finer than a tick, which is all that matters.
 */
export const WIRE_INPUT_CHARGE_MAX_S = 1.5;
export const WIRE_INPUT_CHARGE_BITS = WIRE_UNIT_BITS; // 8

/** Batch size counter. 4 bits → 0..15; the client sends 3 (§2.3), capped here. */
export const WIRE_INPUT_COUNT_BITS = 4;
/** Hard cap on inputs per batch, so a hostile `count` cannot make us loop wild. */
export const MAX_INPUT_BATCH = 8;

function quantizeMove(v: number): number {
  return quantizeFloat(v, -1, 1, WIRE_MOVE_BITS);
}
function dequantizeMove(q: number): number {
  return dequantizeFloat(q, -1, 1, WIRE_MOVE_BITS);
}
function quantizeInputCharge(v: number): number {
  return quantizeFloat(v, 0, WIRE_INPUT_CHARGE_MAX_S, WIRE_INPUT_CHARGE_BITS);
}
function dequantizeInputCharge(q: number): number {
  return dequantizeFloat(q, 0, WIRE_INPUT_CHARGE_MAX_S, WIRE_INPUT_CHARGE_BITS);
}

// ---------------------------------------------------------------------------
// Encode / decode
// ---------------------------------------------------------------------------

/** Serialize one InputCommand's fields (no seq/tick — those are written by the
 *  batch loop). Split out only for readability; not part of the public API. */
function encodeOne(w: BitWriter, cmd: InputCommand): void {
  w.writeBits(quantizeMove(cmd.moveX), WIRE_MOVE_BITS);
  w.writeBits(quantizeMove(cmd.moveZ), WIRE_MOVE_BITS);
  w.writeBits(quantizeYaw(cmd.aimYaw), WIRE_YAW_BITS);
  w.writeBits(quantizeUnitSigned(cmd.liftBias), WIRE_UNIT_BITS);
  w.writeBits(quantizeUnitSigned(cmd.curl), WIRE_UNIT_BITS);
  w.writeBits(quantizeInputCharge(cmd.passCharge), WIRE_INPUT_CHARGE_BITS);
  w.writeBits(quantizeInputCharge(cmd.shootCharge), WIRE_INPUT_CHARGE_BITS);
  w.writeBits(cmd.flags & INPUT_FLAGS_MASK, WIRE_INPUT_FLAGS_BITS);
  w.writeBits(cmd.skill & 0x0f, WIRE_SKILL_BITS);
  w.writeBits(cmd.tactical & 0x07, WIRE_TACTICAL_BITS);
}

function decodeOne(r: BitReader, out: InputCommand): void {
  out.moveX = dequantizeMove(r.readBits(WIRE_MOVE_BITS));
  out.moveZ = dequantizeMove(r.readBits(WIRE_MOVE_BITS));
  out.aimYaw = dequantizeYaw(r.readBits(WIRE_YAW_BITS));
  out.liftBias = dequantizeUnitSigned(r.readBits(WIRE_UNIT_BITS));
  out.curl = dequantizeUnitSigned(r.readBits(WIRE_UNIT_BITS));
  out.passCharge = dequantizeInputCharge(r.readBits(WIRE_INPUT_CHARGE_BITS));
  out.shootCharge = dequantizeInputCharge(r.readBits(WIRE_INPUT_CHARGE_BITS));
  out.flags = r.readBits(WIRE_INPUT_FLAGS_BITS);
  out.skill = r.readBits(WIRE_SKILL_BITS) as SkillMove;
  out.tactical = r.readBits(WIRE_TACTICAL_BITS) as TacticalCommand;
}

/**
 * Write a redundant batch of inputs into `w` (payload only). `batch` is written
 * in its given order; the client's convention is newest-first, but the server
 * dedupes by seq so order is not load-bearing. At most MAX_INPUT_BATCH are sent;
 * a longer array is truncated (the extra redundancy would waste the up budget).
 */
export function encodeInputBatch(w: BitWriter, batch: readonly InputCommand[]): void {
  const count = batch.length < MAX_INPUT_BATCH ? batch.length : MAX_INPUT_BATCH;
  w.writeBits(count, WIRE_INPUT_COUNT_BITS);
  for (let i = 0; i < count; i++) {
    const cmd = batch[i]!;
    w.writeBits(cmd.seq >>> 0, WIRE_SEQ_BITS);
    w.writeBits(cmd.tick >>> 0, WIRE_TICK_BITS);
    encodeOne(w, cmd);
  }
}

/**
 * Decode a batch from `r` into `out`, growing `out` with fresh InputCommands as
 * needed and overwriting existing entries in place (so a server can reuse one
 * scratch array with zero steady-state allocation). Returns the number of inputs
 * decoded. Reads at most MAX_INPUT_BATCH regardless of the wire `count`, so a
 * corrupt frame cannot force an unbounded loop (§2.9). Check `r.overflow` after.
 */
export function decodeInputBatch(r: BitReader, out: InputCommand[]): number {
  let count = r.readBits(WIRE_INPUT_COUNT_BITS);
  if (count > MAX_INPUT_BATCH) count = MAX_INPUT_BATCH;
  for (let i = 0; i < count; i++) {
    let cmd = out[i];
    if (cmd === undefined) {
      cmd = emptyInput();
      out[i] = cmd;
    }
    cmd.seq = r.readBits(WIRE_SEQ_BITS) >>> 0;
    cmd.tick = r.readBits(WIRE_TICK_BITS) >>> 0;
    decodeOne(r, cmd);
  }
  return count;
}
